/**
 * PandaPro — Centralized number/currency formatters
 * Always use these functions for display. Never inline toLocaleString spread.
 */

/** Format a number as Brazilian Real currency: R$ 5.000,00 */
export const formatCurrency = (
  value: number | null | undefined,
  currency: string = 'BRL',
): string => {
  const num = value ?? 0;
  if (!isFinite(num)) return 'R$ 0,00';
  return num.toLocaleString('pt-BR', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

/**
 * Format a number with Brazilian separators, no currency symbol.
 * Example: 5000.5 → "5.000,50"
 */
export const formatNumber = (
  value: number | null | undefined,
  decimals: number = 2,
): string => {
  const num = value ?? 0;
  if (!isFinite(num)) return '0';
  return num.toLocaleString('pt-BR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

/**
 * Format as percentage with pt-BR separators.
 * Example: 12.5 → "12,5%"
 */
export const formatPercent = (
  value: number | null | undefined,
  decimals: number = 1,
): string => {
  const num = value ?? 0;
  if (!isFinite(num)) return '0%';
  return (
    num.toLocaleString('pt-BR', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }) + '%'
  );
};

/**
 * Parse a Brazilian-formatted currency string back to a JS number.
 * Handles: "R$ 5.000,00" → 5000, "5.000,50" → 5000.5, "1234,56" → 1234.56
 */
export const parseCurrency = (str: string): number => {
  if (!str) return 0;
  // Remove currency symbol, spaces and thousand dots; replace comma with dot
  const cleaned = str
    .replace(/[^\d,.-]/g, '')   // keep digits, comma, dot, minus
    .replace(/\.(?=\d{3}(\D|$))/g, '') // remove thousand dots
    .replace(',', '.');
  const num = parseFloat(cleaned);
  return isNaN(num) ? 0 : num;
};

/**
 * Compact form: R$ 1,2k, R$ 4,5M
 */
export const formatCurrencyCompact = (value: number): string => {
  if (!isFinite(value)) return 'R$ 0';
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  if (abs >= 1_000_000) {
    return `${sign}R$ ${(abs / 1_000_000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}M`;
  }
  if (abs >= 1_000) {
    return `${sign}R$ ${(abs / 1_000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}k`;
  }
  return formatCurrency(value);
};
